
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { X } from 'lucide-react';

interface Project {
  id: string
  title: string
  description: string
  category: string
  tech_stack: string[]
  image_url: string | null
  project_url: string | null
  is_featured: boolean
  display_order: number
  is_active: boolean
  problem?: string | null
  solution?: string | null
  impact?: string | null
}

interface ProjectModalProps {
  project: Project | null
  isOpen: boolean
  onClose: () => void
}

const ProjectModal = ({ project, isOpen, onClose }: ProjectModalProps) => {
  if (!project) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold mb-4">{project.title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Project Image */}
          <div className="w-full h-64 md:h-80 overflow-hidden rounded-lg">
            <img 
              src={project.image_url || "/placeholder.svg"} 
              alt={project.title}
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Content Blocks */}
          <div className="grid gap-6 md:grid-cols-1">
            {project.problem && (
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-3 text-goeltec-blue">Problem</h3>
                <p className="text-gray-700 leading-relaxed">{project.problem}</p>
              </div>
            )}
            
            {project.solution && (
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-3 text-goeltec-blue">Solution</h3>
                <p className="text-gray-700 leading-relaxed">{project.solution}</p>
              </div>
            )}
            
            {project.impact && (
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-3 text-goeltec-blue">Impact</h3>
                <p className="text-gray-700 leading-relaxed">{project.impact}</p>
              </div>
            )}
          </div>
          
          {/* Tech Stack */}
          <div>
            <h4 className="font-semibold mb-3">Technologies Used</h4>
            <div className="flex flex-wrap gap-2">
              {project.tech_stack.map((tech) => (
                <span 
                  key={tech} 
                  className="text-xs bg-goeltec-lightblue text-goeltec-blue px-3 py-1 rounded-full"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectModal;
